import moment from 'moment';
import { delay } from 'redux-saga';
import get from 'lodash/get';
import { i18n } from 'fishx';
import isEmpty from 'lodash/isEmpty';
import { message } from '@whalecloud/fdx';
import { getRequestData, BSNL_ATTR, ADDR_DOWN, ADDR_UP, ADDRESS_NAME, ADDRESS_TYPE_FLAG, needCheckAcctModels } from '@/utils';
import {
  qryCertType,
  qryOssAddressListBsnl,
  qryAttrInfoBsnl,
  qryAttrLinkageInfoBsnl,
  qryCustDetail,
  qryCustAddrList,
  qryCert,
  qryContactMan,
  qryAccountList,
  qryAcctDetail,

  addCustAddress,
  sendVerifyCode,
  validVerifyCode,
  qryConfigParam,
  qryConfigParams,
  downloadFile,
  qryOccupationList,
  qrySubsDetail,
  qrySubsPlanDetail,
  qryBilldeliveryMethod,
  qryBillingCycleType,
  createAccount,
  qryAvailablePlanListBsnl,
  qryOfferForChangeBrand,
  qryAvailableRelateOfferBsnl,
  qryEpbxIsdnNbrList,
  qryPackageIdInfo,
  qryBankList,
  checkPinNumberSame4Staff,
  qryFellowNbrType4FMCBsnl,
  qryFellowNbrType4FNFBsnl,
  fmcFixedNumberCheck,
  queryAccountBsnl,
  terminationCheckBsnl,
  qryShiftRange,
  qryExchangeListByAddr,
  qryStdCodeListByExch,
  frServiceInfoCheck,
  unlockedServiceNumber,
} from '@/services/business';
import { qrySubsPlanDetailBsnl } from '@/services/selectPlan';

// 存放不止在一个业务中用到的操作，目前有以下操作
// 查询客户详情
// 查询客户地址
// 查询属性信息
// 查询属性联动信息
// 根据pinCode查询district
// 查询证件列表
// 根据main locality查询sub locality


export default {
  namespace: 'business',
  state: {
    sendTime: 0, // 验证码发送时的时间戳
  },
  effects: {
    * addCustAddress({ payload, modelName }, { call, put }) {
      const addCustAddrResponse = yield call(addCustAddress, payload);
      const { custAddressId } = getRequestData(addCustAddrResponse);
      if (custAddressId) {
        message.success(i18n('BUSINESS_CREATE_ADDRESS_SUCCESSFULLY'));
      }
      yield put({
        type: `${modelName}/save`,
        payload: { custAddressId },
      });
      return custAddressId;
    },

    * qryCustDetail({ payload, modelName }, { call, put }) {
      const response = yield call(qryCustDetail, payload);
      const { custDto, custAttrValueDtoList } = getRequestData(response);

      const attrValues = {};
      (custAttrValueDtoList || []).forEach(attr => {
        if (get(attr, 'attrCode')) {
          attrValues[get(attr, 'attrCode')] = attr;
        }
      });
      const { custId } = payload;
      const { certId, custCode, birthdayDay, custType } = custDto || {};

      // 查询证件信息（POI）和联系人信息
      const [certResp, contactManResp] = yield [call(qryCert, { custId, certId }), call(qryContactMan, { custId })];
      const { certDto } = getRequestData(certResp);
      const { effDate, expDate } = certDto || {};
      const { contactManDtoList = [] } = getRequestData(contactManResp); // 确认过：录入的只会有一个联系人，所以查出来的也只会有一个联系人取[0]就好

      // 客户详情信息里都会有GENDER属性，获取到GENDER属性后需要去查询联动属性TITLE，获取TITLE的可选值
      yield put({
        type: 'qryAttrLinkageInfoBsnl',
        payload: { ...attrValues[BSNL_ATTR.GENDER] },
        linkAttrCode: BSNL_ATTR.TITLE,
        modelName,
      });

      yield put({ type: 'qryCertTypeList', payload: { custType }, modelName });

      // 去查询地址列表
      // 为何把查询地址独立写了个方法？因为好几个地方都会用到查地址
      yield put({ type: 'qryCustAddrList', payload: { custId, addressTypeFlag: ADDRESS_TYPE_FLAG.ALL_ADDR }, modelName });

      // 目前返回的date都是时间戳
      yield put({
        type: `${modelName}/save`,
        payload: {
          custValueMap: {
            ...custDto,
            birthdayDay: moment(birthdayDay),
            ...attrValues,
          },
          custType,
          custId,
          custCode,
          custCertValue: { ...certDto, poiEffDate: moment(effDate), poiExpDate: expDate ? moment(expDate) : undefined }, // 证件的失效日期不再是必填
          custContactMan: contactManDtoList.length > 0 ? contactManDtoList[0] : {},
          poiDefaultFileList: (get(certDto, 'docDtoList') || []).map(doc => ({
            status: 'done',
            uid: doc.docId,
            name: doc.docName,
            attachUrl: doc.docId,
          })),
        },
      });
    },

    * qrySubsDetail({ payload, modelName }, { call, put }) {
      const response = yield call(qrySubsDetail, payload);
      const { subsDto = {}, returnMsg, returnCode } = getRequestData(response, false);

      // 处理 subsDto.installationAddressDto 使其结构和地址列表的接口返回的接口一致
      const { houseNo, road, landmark, latitude, longitude, pinCode, state, district, mainLocalityCode, subLocalityCode, poaDto } = get(subsDto, 'installationAddressDto') || {};
      subsDto.installationAddressDto = pinCode ? {
        ...(get(subsDto, 'installationAddressDto') || {}),
        address1: road,
        address2: houseNo,
        postCode: pinCode,
        addrExtMap: {
          state,
          district,
          mainLocalityCode,
          subLocalityCode,
          landMark: landmark,
          latitude,
          longitude,
          poaDto,
        },
      } : undefined;
      if (returnCode !== '0') { message.error(returnMsg); }
      yield put({ type: `${modelName}/save`, payload: { subsDetail: subsDto } });
      return subsDto;
    },

    * qrySubsPlanDetail({ payload, modelName }, { call, put }) {
      const response = yield call(qrySubsPlanDetail, payload);
      const { subsPlanDetailDto } = getRequestData(response);
      yield put({ type: `${modelName}/save`, payload: { planDetail: subsPlanDetailDto } });
      return subsPlanDetailDto;
    },

    * qryCustAddrList({ payload, modelName }, { call, put }) {
      const { selectedAddrId, custId, addressTypeFlag } = payload;
      const response = yield call(qryCustAddrList, { custId, addressTypeFlag });
      let { custAddrList } = getRequestData(response);
      custAddrList = custAddrList || [];
      // 处理每条地址里poaDto里证件的生失效日期
      (custAddrList || []).forEach(addr => {
        if (get(addr, 'addrExtMap.poaDto')) {
          addr.addrExtMap.poaDto.poaEffDate = moment(get(addr, 'addrExtMap.poaDto.effDate'));
          addr.addrExtMap.poaDto.poaExpDate = get(addr, 'addrExtMap.poaDto.expDate') ? moment(get(addr, 'addrExtMap.poaDto.expDate')) : undefined; // 证件的失效日期不再是必填
        }
      });
      // 因为是已存在客户，默认展示第一个地址
      // 如果前一个页面已经选择过地址，用已选择的地址查询
      // 所以这个时候要根据地址的pinCode去查询可选的state/district/mainLocality，以及根据已选择的mainLocality查询可选的subLocality
      const selectedCustAddr = custAddrList.find(addr => String(addr.custAddressId) === String(selectedAddrId)) || custAddrList[0];
      yield [put({
        type: 'qryAddrListByAddressId',
        payload: { addressLevel: ADDR_DOWN },
        modelName,
        addressName: ADDRESS_NAME.STATE,
      }),
      put({
        type: 'qryAddrListByAddressIdBatch',
        payload: [
          get(selectedCustAddr, 'addrExtMap.state'),
          get(selectedCustAddr, 'addrExtMap.district'),
          get(selectedCustAddr, 'addrExtMap.mainLocalityCode'),
        ],
        modelName,
      })];


      yield put({
        type: `${modelName}/save`,
        payload: {
          custAddrList: custAddrList || [],
          // 因为地址有多个，每个地址又有给自的附件信息
          // 所以，poaDefaultFileList是一个二维数组，里面的每个元素放置的才是对应地址的附件信息，和poiDefaultFileList不一样
          poaDefaultFileList: custAddrList.map(addr => {
            const attachDtoList = get(addr, 'addrExtMap.poaDto.attachDtoList') || [];
            return attachDtoList.map(attach => ({
              status: 'done',
              uid: get(attach, 'attachUrl'),
              name: get(attach, 'attachName'),
              attachUrl: get(attach, 'attachUrl'),
              custAddressId: get(addr, 'custAddressId'),
            }));
          }),
        },
      });
      return custAddrList || [];
    },

    *qryAttrInfoBsnl({ payload, modelName }, { call, put, select }) {
      const attr = yield select(state => state[modelName].attrObj); // 属性查出来后不是全量覆盖，而是增量
      const response = yield call(qryAttrInfoBsnl, payload);
      const { attrDtoList } = getRequestData(response);
      const attrObj = {};
      (attrDtoList || []).forEach(attrDto => {
        if (get(attrDto, 'attrCode')) {
          attrObj[get(attrDto, 'attrCode')] = { valueList: attrDto.attrValueDtoList, attrId: attrDto.attrId };
        }
      });
      yield put({
        type: `${modelName}/save`,
        payload: { attrObj: { ...attr, ...attrObj } }, // 增量覆盖属性信息
      });
      return attrDtoList || [];
    },

    // 查询联动属性
    // 所查询的联动属性需要进行初始化
    *qryAttrLinkageInfoBsnl({ payload, linkAttrCode, modelName }, { call, put, select }) {
      const attrObj = yield select(state => state[modelName].attrObj);
      const response = yield call(qryAttrLinkageInfoBsnl, payload);
      const { baseAttrDtoList } = getRequestData(response);
      // 例如根据gender查询title(linkAttrCode就是title的属性code)，返回的可能是所有与gender有联动的属性，所以这地方要进行过滤，过滤出我们需要的那个属性
      // 根据属性Id进行过滤，属性Id是在初始化的时候就查询过并存储起来的
      const linkedAttrList = (baseAttrDtoList || []).filter(attrDto => get(attrDto, 'baseAttrId') === get(attrObj, `${linkAttrCode}.attrId`));

      yield put({
        type: `${modelName}/save`,
        payload: { attrObj: { ...attrObj, [linkAttrCode]: { valueList: linkedAttrList, attrId: get(attrObj, `${linkAttrCode}.attrId`) } } },
      });
      return baseAttrDtoList || [];
    },
    // 根据pin code查询district，再根据district的addressId向上和向下查询state和main locality
    *qryAddressListByPinCode({ payload, modelName }, { call, put }) {
      const response = yield call(qryOssAddressListBsnl, payload); // 第一次查询，根据pinCode查询district
      const { addrs = [] } = getRequestData(response);
      const { addressId } = get(addrs, '0') || {};
      // 第二次查询，分别向上和向下查询state以及main locality
      const [stateAddrResp, mainLocalityAddrResp] = yield [
        call(qryOssAddressListBsnl, { addressId, addressLevel: ADDR_UP }),
        call(qryOssAddressListBsnl, { addressId, addressLevel: ADDR_DOWN }),
      ];
      const { addrs: stateAddrOpts } = getRequestData(stateAddrResp);
      const { addrs: mainLocalityOpts } = getRequestData(mainLocalityAddrResp);
      yield put({
        type: `${modelName}/save`,
        payload: {
          stateAddrOpts: stateAddrOpts || [],
          mainLocalityOpts: mainLocalityOpts || [],
          districtOpts: addrs || [],
        } });
      return true;
    },

    // 根据main locality查询sub locality
    * qryAddrListByMainLocality({ payload, modelName }, { call, put }) {
      const response = yield call(qryOssAddressListBsnl, payload);
      const { addrs } = getRequestData(response);
      yield put({ type: `${modelName}/save`, payload: { subLocalityOpts: addrs || [] } });
      return true;
    },
    // 根据addressId查询下级地址信息
    * qryAddrListByAddressId({ payload, modelName, addressName }, { call, put }) {
      const response = yield call(qryOssAddressListBsnl, payload);
      const { addrs } = getRequestData(response);
      yield put({ type: `${modelName}/save`, payload: { [addressName]: addrs || [] } });
      return true;
    },
    * qryAddrListByAddressIdBatch({ payload, modelName }, { put }) {
      yield [
        put({
          type: 'qryAddrListByAddressId',
          payload: { addressLevel: ADDR_DOWN, addressId: payload[0] },
          modelName,
          addressName: ADDRESS_NAME.CITY,
        }),
        put({
          type: 'qryAddrListByAddressId',
          payload: { addressLevel: ADDR_DOWN, addressId: payload[1] },
          modelName,
          addressName: ADDRESS_NAME.MAIN_LOCALITY,
        }),
        put({
          type: 'qryAddrListByAddressId',
          payload: { addressLevel: ADDR_DOWN, addressId: payload[2] },
          modelName,
          addressName: ADDRESS_NAME.SUB_LOCALITY,
        }),
      ];
    },

    * qryShiftRange({ payload, modelName }, { call, put }) {
      const response = yield call(qryShiftRange, payload);
      const { shiftRangeName } = getRequestData(response);
      yield put({ type: `${modelName}/save`, payload: { shiftRange: shiftRangeName || '' } });
    },

    * frServiceInfoCheck({ payload }, { call }) {
      const response = yield call(frServiceInfoCheck, payload);
      return getRequestData(response);
    },

    * qryCertTypeList({ payload, modelName }, { call, put }) {
      const response = yield call(qryCertType, payload);
      const { certTypeList } = getRequestData(response);
      yield put({ type: `${modelName}/save`, payload: { certTypeList: certTypeList || [] } });
    },

    * qryPOICertTypeList({ payload, modelName }, { call, put }) {
      const response = yield call(qryCertType, { ...payload, classification: 'I' });
      const { certTypeList } = getRequestData(response);
      yield put({ type: `${modelName}/save`, payload: { POICertTypeList: certTypeList || [] } });
    },
    * qryPOACertTypeList({ payload, modelName }, { call, put }) {
      const response = yield call(qryCertType, { ...payload, classification: 'A' });
      const { certTypeList } = getRequestData(response);
      yield put({ type: `${modelName}/save`, payload: { POACertTypeList: certTypeList || [] } });
    },
    * qryAccountList({ payload, modelName }, { call, put }) {
      const response = yield call(qryAccountList, payload);
      let { acctDtoList } = getRequestData(response);
      // 只显示netType是A或者P的账户
      acctDtoList = (acctDtoList || []).filter(item => (item.netType === 'A' || item.netType === 'P'));
      yield put({ type: `${modelName}/save`, payload: { acctDtoList } });
      return acctDtoList;
    },

    *qryAcctDetail({ payload, modelName, needCheckAcct }, { call, put, select }) {
      const response = yield call(qryAcctDetail, payload);
      const acctDetail = getRequestData(response);
      let isAvailable = true; // 选择的账户是否可用
      if (needCheckAcct && needCheckAcctModels.includes(modelName)) {
        // 移机需要对账户进行筛选
        const oldAcctDetail = yield select(state => state[modelName].acctDetail) || {};
        // 1、账户是否可用
        const { acctTypeCode, acctSubTypeCode } = acctDetail;
        if (acctTypeCode !== oldAcctDetail.acctTypeCode && acctSubTypeCode !== oldAcctDetail.acctSubTypeCode) {
          // 提示该账号不符合要求
          isAvailable = false;
        }
      }
      if (!isAvailable) {
        // 账户不满足需求，直接返回
        return {};
      }
      // 处理 acctDetail.billingAddressDto 使其结构和地址详情的接口返回的接口一致
      // 都是地址信息，很难相信他们字段居然还不一致
      const { houseNo, road, landmark, latitude, longitude, pinCode, state, district, mainLocalityCode, subLocalityCode, poaDto = {} } = get(acctDetail, 'billingAddressDto') || {};
      poaDto.poaEffDate = moment(poaDto.effDate);
      poaDto.poaExpDate = moment(poaDto.expDate);
      acctDetail.billingAddressDto = pinCode ? {
        ...(get(acctDetail, 'billingAddressDto') || {}),
        address1: road,
        address2: houseNo,
        postCode: pinCode,
        addrExtMap: {
          state,
          district,
          mainLocalityCode,
          subLocalityCode,
          landMark: landmark,
          latitude,
          longitude,
          poaDto,
        },
      } : undefined;
      yield [
        put({
          type: 'qryAttrLinkageInfoBsnl',
          payload: {
            attrCode: BSNL_ATTR.ACCOUNT_TYPE,
            attrValue: get(acctDetail, 'acctTypeCode'),
          },
          linkAttrCode: BSNL_ATTR.ACCT_SUB_TYPE,
          modelName,
        }),
      ];

      yield put({ type: `${modelName}/save`, payload: { acctDetail } });
      return acctDetail;
    },

    * sendVerifyCode({ payload }, { call, put, select }) {
      const sendTime = yield select(state => state.business.sendTime);
      const times = yield select(state => state.business.DRM_VERIFY_CODE_RESEND_TIME);
      if (Date.now() - sendTime < times * 1000) {
        message.warn(i18n('BUSINESS_TOO_OFTEN'));
        return false;
      }
      const response = yield call(sendVerifyCode, payload);
      const { returnCode, securityCode } = getRequestData(response);
      if (returnCode === '0') {
        yield put({
          type: 'timing',
        });
        yield put({
          type: 'save',
          payload: { sendTime: Date.now() },
        });
        return securityCode;
      }
      return false;
    },

    * sendVerifyCodeReturnAll({ payload }, { call, put, select }) {
      const sendTime = yield select(state => state.business.sendTime);
      const times = yield select(state => state.business.DRM_VERIFY_CODE_RESEND_TIME);
      if (Date.now() - sendTime < times * 1000) {
        message.warn(i18n('BUSINESS_TOO_OFTEN'));
        return false;
      }
      const response = yield call(sendVerifyCode, payload);
      const data = getRequestData(response);
      const { returnCode } = data;
      if (returnCode === '0') {
        yield put({
          type: 'timing',
        });
        yield put({
          type: 'save',
          payload: { sendTime: Date.now() },
        });
        return data;
      }
      return false;
    },

    * validVerifyCode({ payload }, { call, put }) {
      const response = yield call(validVerifyCode, payload);
      const { returnCode } = getRequestData(response);
      if (returnCode === '0') {
        yield put({
          type: 'save',
          payload: { sendTime: 0 }, // 验证成功，刷新发送时间，意味着下一次发送验证码间隔不用小于120s ---- 2022.1.19 来自黄凡在演示前的要求
        });
      }
      return returnCode === '0';
    },

    * qryConfigParam({ payload }, { call, put }) {
      const response = yield call(qryConfigParam, payload);
      const { paramValue, defaultValue } = getRequestData(response);

      yield put({
        type: 'save',
        payload: { [payload.configCode]: paramValue || defaultValue },
      });
    },

    * qryConfigParams({ payload }, { call, put }) {
      const response = yield call(qryConfigParams, payload);
      const paramsList = getRequestData(response);
      const paramsObj = {};
      if (!isEmpty(paramsList)) {
        paramsList.forEach(item => {
          if (item) {
            paramsObj[item.paramCode] = item.paramValue || item.defaultValue;
          }
        });
      }

      yield put({
        type: 'save',
        payload: { ...paramsObj },
      });
    },
    * downloadFile({ payload }, { call }) {
      if (!window.downloadFileFlag) {
        window.downloadFileFlag = true;
      } else {
        window.downloadFileFlag = false;
        return;
      }
      const { docName, docId } = payload;
      const imgTypeList = ['png', 'jpeg', 'jpg', 'bmp', 'tiff'];
      // 如果不是图片，则下载后预览
      const response = yield call(downloadFile, { docId });
      const blob = new Blob([response.data], { type: 'application/octet-stream' });
      const dotIndex = docName.lastIndexOf('.');
      const suffix = docName.substring(dotIndex + 1);
      if (imgTypeList.includes(suffix.toLowerCase())) {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onload = () => {
          const prevBoxMask = document.createElement('div'); // 遮罩
          const prevBox = document.createElement('div');
          const closeBtn = document.createElement('span');
          const img = new Image();
          prevBoxMask.setAttribute('class', 'prevBoxMask');
          prevBox.setAttribute('class', 'previewBox');
          closeBtn.setAttribute('class', 'prevBoxCloseBtn');
          closeBtn.onclick = e => {
            e.stopPropagation();
            document.body.removeChild(prevBoxMask);
          };
          prevBoxMask.appendChild(prevBox);

          prevBox.appendChild(img);
          prevBox.appendChild(closeBtn);

          img.src = reader.result;
          document.body.appendChild(prevBoxMask);
          img.onerror = () => {
            message.error('Preview image failed, please try again later.');
            document.body.removeChild(prevBoxMask);
          };
          prevBoxMask.onclick = e => {
            e.stopPropagation();
            document.body.removeChild(prevBoxMask);
          };
        };
      } else {
        // 创建新的URL并指向File对象或者Blob对象的地址
        const blobURL = window.URL.createObjectURL(blob);
        // 创建a标签，用于跳转至下载链接
        const tempLink = document.createElement('a');
        tempLink.setAttribute('class', 'reviewLinkDownload');
        tempLink.style.display = 'none';
        tempLink.href = blobURL;

        tempLink.setAttribute('download', decodeURI(docName));
        // 兼容：某些浏览器不支持HTML5的download属性
        if (typeof tempLink.download === 'undefined') {
          tempLink.setAttribute('target', '_blank');
        }
        // 挂载a标签
        document.body.appendChild(tempLink);
        tempLink.click();
        document.body.removeChild(tempLink);
        // 释放blob URL地址
        window.URL.revokeObjectURL(blobURL);
      }
      window.downloadFileFlag = false;
    },

    * timing(_, { put, select, cancel }) { // 验证码的倒计时
      let times = yield select(state => state.business.DRM_VERIFY_CODE_RESEND_TIME);

      yield put({
        type: 'save',
        payload: { timing: true, times },
      });
      for (; times >= 0; times -= 1) {
        const timing = yield select(state => state.business.timing);
        if (!timing) { // 如果循环还未完成，timing就被置为false了，说明当前循环需要跳出,同时取消当前的这个任务
          yield cancel();
          break;
        }
        yield put({
          type: 'save',
          payload: { times },
        });
        yield delay(1000);
      }
      yield put({
        type: 'save',
        payload: { timing: false, times, resend: true },
      });
    },
    * qryOccupationList({ payload, modelName }, { call, put }) {
      const response = yield call(qryOccupationList, payload);
      const { occupationDtoList } = getRequestData(response);
      yield put({ type: `${modelName}/save`, payload: { occupationList: occupationDtoList || [] } });
    },

    // 查询账单寄送方式(账单介质)
    * qryBilldeliveryMethod({ modelName }, { call, put }) {
      const response = yield call(qryBilldeliveryMethod);
      const { deliveryMethodDtoList } = getRequestData(response);

      yield put({
        type: `${modelName}/save`,
        payload: { deliveryMethodList: deliveryMethodDtoList },
      });
    },
    // 查询账期
    * qryBillingCycleType({ payload, modelName }, { call, put }) {
      const response = yield call(qryBillingCycleType, payload);
      const { billingCycleTypeList } = getRequestData(response);

      // 注意，这里返回的billingCycleTypeId是数字类型，但是从账户详情里返回的billingCycleTypeId却是字符串类型，为了保持类型一致，这里转换为字符串类型
      yield put({
        type: `${modelName}/save`,
        payload: {
          billingCycleTypeList: (billingCycleTypeList || []).map(cycle => ({ ...cycle, billingCycleTypeId: String(cycle.billingCycleTypeId) })),
        },
      });
    },
    *createAccount({ payload, modelName }, { call, put }) {
      const response = yield call(createAccount, payload);
      const { accountCode, acctId } = getRequestData(response);
      if (acctId) {
        message.success(i18n('BUSINESS_CREATE_ACCOUNT_SUCCESSFULLY'));
      }
      yield put({
        type: `${modelName}/save`,
        payload: { accountCode, acctId },
      });
      return acctId;
    },
    * qryAvailablePlanListBsnl({ payload, modelName }, { call, put }) {
      const result = yield call(qryAvailablePlanListBsnl, payload);
      const { offerDtoList, pageReqDto } = getRequestData(result);
      const { total } = pageReqDto;
      yield put({
        type: `${modelName}/save`,
        payload: { offerDtoList: offerDtoList || [], pageInfo: pageReqDto, total },
      });
    },
    * qryOfferForChangeBrand({ payload, modelName }, { call, put, select }) {
      const { pageSize, totalRecord, selectSubsInfo } = yield select(state => ({
        pageSize: state[modelName].pageSize,
        totalRecord: state[modelName].totalRecord,
        selectSubsInfo: state[modelName].selectSubsInfo,
      }));
      const subsId = get(selectSubsInfo, 'subsId');
      const result = yield call(qryOfferForChangeBrand, { ...payload, subsId });
      const { availableOfferDtoList, pageDto } = getRequestData(result);
      yield put({
        type: `${modelName}/save`,
        payload: {
          availableOfferDtoList: availableOfferDtoList || [],
          pageInfo: pageDto,
          totalRecord: pageDto.totalRecord || totalRecord,
          pageSize: pageDto.pageCount || pageSize,
        },
      });
      return { offerList: availableOfferDtoList || [], pageDto };
    },
    // 查询所有可订购的vas
    * qryAvailableRelateOfferBsnl({ payload, modelName }, { call, put }) {
      const result = yield call(qryAvailableRelateOfferBsnl, payload);
      const { offerDtoList } = getRequestData(result);
      yield put({
        type: `${modelName}/save`,
        payload: { featureList: offerDtoList || [] },
      });
    },

    * qryMainProductIdByOfferId({ payload, modelName }, { call, put }) {
      const response = yield call(qrySubsPlanDetailBsnl, payload);
      const { subsPlanDetailDto } = getRequestData(response);
      const mainProcutId = get(subsPlanDetailDto, 'indepProdSpecDto.indepProdSpecId');
      yield put({
        type: `${modelName}/save`,
        payload: { mainProcutId },
      });
      return mainProcutId;
    },
    // 查询ISDN主号码 qryEpbxIsdnNbrList
    * qryEpbxIsdnNbrList({ payload, modelKey }, { call, put }) {
      const response = yield call(qryEpbxIsdnNbrList, payload);
      const { epbxIsdnNbrDtoList = [] } = getRequestData(response);
      yield put({
        type: `${modelKey}/save`,
        payload: { isdnNumberList: epbxIsdnNbrDtoList },
      });
      return epbxIsdnNbrDtoList;
    },
    // 查询宽带带宽
    * qryPackageIdInfo({ payload, modelKey }, { call, put }) {
      const response = yield call(qryPackageIdInfo, payload);
      const { packageIdDtoList = [] } = getRequestData(response);
      yield put({
        type: `${modelKey}/save`,
        payload: { packageIdList: packageIdDtoList },
      });
      return packageIdDtoList;
    },
    // 查询银行列表
    * qryBankList({ payload, modelName }, { call, put }) {
      const resp = yield call(qryBankList, payload);
      yield put({
        type: `${modelName}/save`,
        payload: { bankDtoList: get(getRequestData(resp), 'bankDtoList') || [] },
      });
      return getRequestData(resp);
    },
    // 校验pin code是否正确
    * checkPinNumberSame4Staff({ payload }, { call }) {
      const response = yield call(checkPinNumberSame4Staff, payload);
      const { isSame } = getRequestData(response, false);
      return isSame;
    },
    // 查询FMC Type
    *qryFellowNbrType4FMCBsnl(_, { call }) {
      const response = yield call(qryFellowNbrType4FMCBsnl);
      const { fellowNbrTypeDtoList } = getRequestData(response);
      return fellowNbrTypeDtoList || [];
    },
    // 查询FNF Type
    *qryFellowNbrType4FNFBsnl(_, { call }) {
      const response = yield call(qryFellowNbrType4FNFBsnl);
      const { fellowNbrTypeDtoList } = getRequestData(response);
      return fellowNbrTypeDtoList || [];
    },
    // fmcFixedNumber号码校验
    *fmcFixedNumberCheck({ payload, modelKey }, { call, select }) {
      const { addressInfo } = yield select(state => state[modelKey].selectService || {});
      const { subsId } = yield select(state => state[modelKey].subsDetail || {});
      const params = {};
      if (subsId) {
        params.subsId = subsId;
      } else {
        params.custAddressId = addressInfo.addressId;
      }
      const response = yield call(fmcFixedNumberCheck, { ...payload, ...params });
      return getRequestData(response, false);
    },
    // 查询欠费
    *queryAccountBsnl({ payload }, { call }) {
      const response = yield call(queryAccountBsnl, payload);
      return getRequestData(response);
    },
    // 拆机订户协议期校验
    *terminationCheckBsnl({ payload }, { call }) {
      const response = yield call(terminationCheckBsnl, payload);
      return response.data;
    },
    // 根据地址查询exchangeCode
    *qryExchangeListByAddr({ payload }, { call }) {
      const response = yield call(qryExchangeListByAddr, payload);
      return getRequestData(response);
    },

    // 根据exchangeId查询StdCode
    *qryStdCodeListByExch({ payload }, { call }) {
      const response = yield call(qryStdCodeListByExch, payload);
      return getRequestData(response);
    },

    // 号码locked解锁接口
    *unlockedServiceNumber({ payload }, { call }) {
      const response = yield call(unlockedServiceNumber, payload);
      const data = getRequestData(response);
      const { returnCode } = data;
      if (returnCode !== '0') {
        message.warn(i18n('SUBSINFO_UNLOCKED_NUMBER_FAILED'));
      }
      return returnCode;
    },
  },

  reducers: {
    save(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },
  },
};
